import type { shell } from 'punica';
import { isHeadlessScope } from '@punica/editor';
import { emitShellEvent } from '@punica/editor';

import { Helpers } from '../view/helpers.js';

type LayoutProfile = shell.Layout.LayoutProfile;
type LayoutSlotId = shell.Layout.LayoutSlotId;
type LayoutSlot = shell.Layout.LayoutSlot;
type LayoutNode = shell.Layout.LayoutNode;
type ILayoutManager = shell.Layout.ILayoutManager;
type ApplicationProfile = shell.Profile.ApplicationProfile;

const classicEditorProfile: LayoutProfile = {
  id: 'classic-editor',
  title: 'Classic Editor',
  root: {
    // Main editor area: content on top, panel at the bottom. The new
    // contentSidebar drawer is an overlay slot created in applyProfile,
    // separate from this split tree. The legacy secondarySidebar slot
    // (used by some hosts) remains part of the layout tree.
    type: 'row',
    sizes: [70, 30],
    children: [
      {
        type: 'column',
        sizes: [80, 20],
        children: [
          { type: 'slot', id: 'content', role: 'content' },
          { type: 'slot', id: 'panel', role: 'panel' }
        ]
      },
      { type: 'slot', id: 'secondarySidebar', role: 'secondarySidebar' }
    ]
  }
};

const welcomeProfile: LayoutProfile = {
  id: 'welcome',
  title: 'Welcome',
  root: {
    type: 'row',
    sizes: [100],
    children: [
      {
        type: 'column',
        sizes: [100],
        children: [{ type: 'slot', id: 'content', role: 'content' }]
      }
    ]
  }
};

/**
 * Slot roles whose pane keeps its pixel size when the container resizes; the
 * content pane absorbs the difference. See `#pinChromePanes`.
 */
const PINNED_PANE_ROLES = new Set(['secondarySidebar', 'panel']);

/** A pane may not be squeezed below this share — matches punica-split's floor. */
const MIN_PANE_PCT = 5;

/** Quiet period after the last resize before pane animation is restored. */
const PANE_SETTLE_MS = 150;

/**
 * Re-express pane percentages so the pinned panes keep the pixel size they had
 * at `prevPx` while the container becomes `nextPx`. Returns null when the
 * request cannot be honoured (degenerate sizes, or no room left for the
 * flexible panes) so the caller leaves the split alone.
 */
export function sizesPreservingPx(
  sizes: number[],
  prevPx: number,
  nextPx: number,
  pinned: number[],
  minPct: number = MIN_PANE_PCT
): number[] | null {
  if (!sizes.length || prevPx <= 0 || nextPx <= 0) return null;
  const flexible = sizes
    .map((_, index) => index)
    .filter((index) => !pinned.includes(index));
  if (!flexible.length) return null;

  const next = [...sizes];
  let pinnedTotal = 0;
  for (const index of pinned) {
    const px = (sizes[index] / 100) * prevPx;
    const pct = Math.max(minPct, (px / nextPx) * 100);
    next[index] = pct;
    pinnedTotal += pct;
  }

  const remaining = 100 - pinnedTotal;
  if (remaining < minPct * flexible.length) return null;

  const flexibleBefore = flexible.reduce((sum, index) => sum + sizes[index], 0);
  for (const index of flexible) {
    next[index] =
      flexibleBefore > 0
        ? (sizes[index] / flexibleBefore) * remaining
        : remaining / flexible.length;
  }

  // setSizes() rejects anything that does not add up to 100, so absorb the
  // rounding drift into the largest flexible pane.
  const total = next.reduce((sum, value) => sum + value, 0);
  const widest = flexible.reduce((a, b) => (next[a] >= next[b] ? a : b));
  next[widest] += 100 - total;
  return next;
}

/** Default minimal root — single content slot, used when no layout is declared. */
const DEFAULT_MINIMAL_ROOT: LayoutNode = {
  type: 'row',
  sizes: [100],
  children: [{ type: 'slot', id: 'content', role: 'content' }]
};

class LayoutManager implements ILayoutManager {
  #layoutElement: HTMLElement;
  #slots: Map<LayoutSlotId, LayoutSlot> = new Map();
  #viewHosts: Map<string, Map<string, HTMLElement>> = new Map();
  #activeProfile: LayoutProfile;
  #collapsed: Map<LayoutSlotId, number[]> = new Map();
  #paneObservers: ResizeObserver[] = [];

  constructor() {
    this.#layoutElement = document.createElement('div');
    this.#layoutElement.id = 'punica-editor';
    this.#activeProfile = classicEditorProfile;
  }

  applyProfile(profile: LayoutProfile): void {
    this.#activeProfile = profile;
    this.#slots.clear();
    this.#viewHosts.clear();
    this.#collapsed.clear();

    this.#disconnectPaneObservers();
    this.#layoutElement.innerHTML = '';
    // Mark current profile on the root element so host CSS can adapt layout.
    this.#layoutElement.classList.remove('layout-welcome', 'layout-classic');
    this.#layoutElement.classList.add(
      profile.id === 'welcome' ? 'layout-welcome' : 'layout-classic'
    );

    // Activity bar host (left column in CSS grid) – only for editor profile.
    if (profile.id !== 'welcome') {
      const activityBarHost = document.createElement('div');
      activityBarHost.id = 'activityBar';
      this.#layoutElement.appendChild(activityBarHost);
      this.#slots.set('activityBar', {
        id: 'activityBar',
        role: 'activityBar',
        content: activityBarHost
      });
    }

    // Main content area (editor + secondary sidebar + panel)
    const mainContent = document.createElement('div');
    mainContent.id = 'mainContent';
    mainContent.style.height = '100%';
    mainContent.style.width = '100%';
    mainContent.style.display = 'flex';
    this.#layoutElement.appendChild(mainContent);

    // Primary sidebar drawer (absolute overlay) – only for editor profile.
    if (profile.id !== 'welcome') {
      const primarySidebarHost = document.createElement('div');
      primarySidebarHost.id = 'primarySidebar';
      this.#layoutElement.appendChild(primarySidebarHost);
      this.#slots.set('primarySidebar', {
        id: 'primarySidebar',
        role: 'primarySidebar',
        content: primarySidebarHost
      });

      // Content sidebar drawer (absolute overlay).
      const contentSidebarHost = document.createElement('div');
      contentSidebarHost.id = 'contentSidebar';
      this.#layoutElement.appendChild(contentSidebarHost);
      this.#slots.set('contentSidebar', {
        id: 'contentSidebar',
        role: 'overlay',
        content: contentSidebarHost
      });
    }

    // Status bar host (bottom row in CSS grid)
    const statusbarHost = document.createElement('div');
    statusbarHost.id = 'statusbar';
    this.#layoutElement.appendChild(statusbarHost);
    this.#slots.set('statusbar', {
      id: 'statusbar',
      role: 'statusbar',
      content: statusbarHost
    });

    // Build the configurable layout tree inside mainContent.
    this.#buildNode(profile.root, mainContent, true);
  }

  getActiveProfile(): LayoutProfile {
    return this.#activeProfile;
  }

  getSlot(id: LayoutSlotId): LayoutSlot | undefined {
    return this.#slots.get(id);
  }

  /**
   * Create (or re-attach) the DOM host for a view WITHOUT any visibility
   * side effect. New hosts start hidden; nothing else in the slot is
   * touched. This is the visibility-neutral half of the old
   * `ensureViewHost` — the reveal half lives in `showView`, callable
   * only through `punica.shell.Views.manager.reveal(viewId, source)`.
   */
  materializeViewHost(slotId: LayoutSlotId, viewId: string): HTMLElement {
    const slot = this.#slots.get(slotId);
    if (!slot) {
      throw new Error(`Layout slot "${slotId}" not found in active profile`);
    }

    let hostsForSlot = this.#viewHosts.get(slotId);
    if (!hostsForSlot) {
      hostsForSlot = new Map();
      this.#viewHosts.set(slotId, hostsForSlot);
    }

    // Tabbed dock areas (bottom panel + right sidebar) route view hosts into
    // the dedicated views root created by their TabbedContainer manager, so the
    // tab strip and the view content share the slot correctly. The container
    // marks that root with `data-views-root`; fall back to the slot content
    // when no tabbed container has built one (e.g. minimal profiles).
    let container: HTMLElement = slot.content;
    if (slotId === 'panel' || slotId === 'secondarySidebar') {
      const viewsRoot =
        slot.content.querySelector<HTMLElement>('[data-views-root]');
      if (viewsRoot) {
        container = viewsRoot;
      }
    }

    let host = hostsForSlot.get(viewId);
    if (!host) {
      host = document.createElement('div');
      host.dataset.viewId = viewId;
      host.style.height = '100%';
      host.style.width = '100%';
      if (slotId === 'primarySidebar') {
        // The view scrolls, not the sidebar. `#primarySidebar` is the sidebar's
        // chrome: it carries the separator and the resize handle as absolutely
        // positioned children, and those scroll away with the content whenever
        // the chrome itself is the scroll container — the separator stopped
        // partway down as soon as a long view was scrolled. Moving the
        // scrollport one level in keeps the same visible rect (the host fills
        // the sidebar), so sticky view headers and the scrollbar position are
        // unchanged. Views with their own inner scroller never overflow this
        // box and are unaffected.
        host.style.overflowX = 'hidden';
        host.style.overflowY = 'auto';
      }
      // Materialized-but-hidden until showView reveals it.
      host.style.display = 'none';
      container.appendChild(host);
      hostsForSlot.set(viewId, host);
    } else if (!host.parentElement) {
      // The host was removed from the DOM (e.g. by a contextual sidebar
      // toggle). Re-attach it to the current container so extensions can
      // safely recreate their contents.
      container.appendChild(host);
    }

    return host;
  }

  /**
   * Make a materialized view the visible one in its slot: hide siblings,
   * show the slot, reset the contextual contentSidebar overlay when the
   * primary sidebar switches, and sync the ActivityBar selection.
   *
   * Visibility authority: only `punica.shell.Views.manager.reveal`
   * (via the `shell.revealView` capability) and the legacy
   * `ensureViewHost` compatibility path may call this.
   */
  showView(slotId: LayoutSlotId, viewId: string): void {
    // When a new view is shown in the primarySidebar, close any contextual
    // contentSidebar overlay (e.g. Kaggle datasets drawer). Primary sidebar
    // is the main navigation for side views, so switching it should reset
    // transient helpers.
    if (slotId === 'primarySidebar') {
      this.resetContentSidebar();
    }

    const slot = this.#slots.get(slotId);
    if (!slot) {
      throw new Error(`Layout slot "${slotId}" not found in active profile`);
    }

    const hostsForSlot = this.#viewHosts.get(slotId);
    hostsForSlot?.forEach((el, id) => {
      el.style.display = id === viewId ? 'block' : 'none';
    });

    slot.content.style.display = 'block';

    // Keep ActivityBar selection in sync with the currently visible
    // primarySidebar view, even when the view is shown directly (e.g. on
    // startup or via LayoutManager).
    if (slotId === 'primarySidebar') {
      try {
        punica.shell.ActivityBar.manager.setActiveByViewId?.(viewId);
      } catch (err) {
        // best-effort only; ActivityBar errors must not break view host setup.
        console.warn('[Layout] Failed to sync ActivityBar selection:', err);
      }
    }
  }

  /**
   * Tear down the transient contentSidebar overlay (e.g. the extension detail
   * or Kaggle datasets drawer). Removes its view hosts, clears the slot DOM and
   * notifies the Views registry so providers dispose and re-render on their
   * next reveal. Called when the primary sidebar switches views, is dismissed,
   * or a full-page content tab takes the foreground — the helper is contextual
   * to the sidebar and would otherwise linger as a zombie overlay.
   */
  resetContentSidebar(): void {
    const contentSlot = this.#slots.get('contentSidebar');
    if (!contentSlot) return;
    const hostsForContent = this.#viewHosts.get('contentSidebar');
    if (hostsForContent) {
      hostsForContent.forEach((hostEl) => {
        if (hostEl.parentElement) {
          hostEl.parentElement.removeChild(hostEl);
        }
      });
      hostsForContent.clear();
    }
    contentSlot.content.innerHTML = '';
    try {
      (punica.shell as any)?.Views?.manager?.notifySlotReset?.(
        'contentSidebar'
      );
    } catch {
      /* best-effort; Views module may be absent in minimal profiles */
    }
  }

  /** Hide every view host in a slot (used by Views.hide). */
  hideSlotViews(slotId: LayoutSlotId): void {
    const hostsForSlot = this.#viewHosts.get(slotId);
    hostsForSlot?.forEach((el) => {
      el.style.display = 'none';
    });
  }

  /**
   * Locate the `punica-split` pane a slot occupies, if any. Slots rendered as
   * absolute overlay drawers (primarySidebar / contentSidebar) are not inside a
   * split and return undefined — collapsing is a no-op for them.
   */
  #splitPaneFor(
    slotId: LayoutSlotId
  ): { split: any; index: number; count: number } | undefined {
    const slot = this.#slots.get(slotId);
    if (!slot) return undefined;
    const split = slot.content.closest('punica-split') as any;
    if (!split) return undefined;
    // The split lays out its DIRECT element children as panes; walk up from the
    // slot container to whichever direct child holds it.
    let child: HTMLElement | null = slot.content;
    while (child && child.parentElement !== split) {
      child = child.parentElement;
    }
    if (!child) return undefined;
    const panes = Array.from(split.children).filter(
      (c: any) => c.nodeType === 1
    );
    const index = panes.indexOf(child);
    if (index < 0) return undefined;
    return { split, index, count: panes.length };
  }

  /** Whether a slot is currently collapsed to zero size in its split. */
  isSlotCollapsed(slotId: LayoutSlotId): boolean {
    return this.#collapsed.has(slotId);
  }

  /**
   * Collapse a slot's split pane to zero (fully hiding the region — the pane
   * and its gutter disappear) or expand it back to the remembered sizes. Used
   * by the ViewRegistry so hiding the last visible view empties the region and
   * revealing one restores it. No-op for non-split (overlay) slots.
   */
  setSlotCollapsed(slotId: LayoutSlotId, collapsed: boolean): void {
    const pane = this.#splitPaneFor(slotId);
    if (!pane) return;
    const { split, index, count } = pane;
    if (typeof split.setSizes !== 'function') return;

    if (collapsed) {
      if (this.#collapsed.has(slotId)) return;
      const sizes: number[] = (split.getSizes?.() ?? []).slice();
      if (sizes.length !== count || count < 2) return;
      this.#collapsed.set(slotId, sizes);
      const others = sizes.reduce(
        (sum, s, i) => (i === index ? sum : sum + s),
        0
      );
      const target = sizes.map((s, i) => {
        if (i === index) return 0;
        return others > 0 ? (s / others) * 100 : 100 / (count - 1);
      });
      split.setSizes(target);
    } else {
      const prev = this.#collapsed.get(slotId);
      this.#collapsed.delete(slotId);
      if (prev && prev.length === count) split.setSizes(prev);
    }
  }

  /** Remove a view host from the DOM entirely (extension deactivation). */
  removeViewHost(slotId: LayoutSlotId, viewId: string): void {
    const hostsForSlot = this.#viewHosts.get(slotId);
    const host = hostsForSlot?.get(viewId);
    if (host) {
      if (host.parentElement) host.parentElement.removeChild(host);
      hostsForSlot?.delete(viewId);
    }
  }

  /**
   * LEGACY compatibility surface — materialize + reveal in one call.
   *
   * Pre-migration extensions call this imperatively (often from inside
   * `activate()`), which is exactly the coupling the ViewRegistry
   * removes. Behavior:
   *   - interactive contexts: full legacy behavior (materialize + show)
   *   - headless activation scope (capability-triggered activation):
   *     the host is materialized HIDDEN, a `shell.view.revealBlocked`
   *     event is emitted and a deprecation warning is logged.
   *
   * Migrated extensions must not call this for visibility — register a
   * ViewProvider and let `shell.revealView` decide when to show.
   */
  ensureViewHost(slotId: LayoutSlotId, viewId: string): HTMLElement {
    const host = this.materializeViewHost(slotId, viewId);

    if (isHeadlessScope()) {
      emitShellEvent({
        name: 'shell.view.revealBlocked',
        source: 'shell:views',
        kind: 'warn',
        payload: { viewId, slot: slotId, api: 'Layout.ensureViewHost' }
      });
      // eslint-disable-next-line no-console
      console.warn(
        `[Shell] UI mutation during headless activation blocked: ensureViewHost("${slotId}", "${viewId}"). ` +
          'Register a ViewProvider (context.ui.registerViewProvider) and reveal via the shell.revealView capability instead.'
      );
      return host;
    }

    this.showView(slotId, viewId);
    return host;
  }

  #buildNode(node: LayoutNode, parent: HTMLElement, isRoot: boolean): void {
    if (node.type === 'slot') {
      const slotContainer = document.createElement('div');
      slotContainer.style.height = '100%';
      slotContainer.style.width = '100%';
      slotContainer.style.overflow = 'hidden';
      parent.appendChild(slotContainer);

      const slot: LayoutSlot = {
        id: node.id,
        role: node.role,
        content: slotContainer
      };
      this.#slots.set(node.id, slot);
      return;
    }

    const isRow = node.type === 'row';
    const split = Helpers.createSplit({
      orientation: isRow ? 'horizontal' : 'vertical',
      sizes: node.sizes,
      style: { height: '100%', width: '100%' }
    });

    parent.appendChild(split);

    const childCount = node.children.length;
    for (let i = 0; i < childCount; i++) {
      const childContainer = document.createElement('div');
      childContainer.style.height = '100%';
      childContainer.style.width = '100%';
      split.appendChild(childContainer);
      this.#buildNode(node.children[i], childContainer, false);
    }

    this.#pinChromePanes(split, node);
  }

  /**
   * Keep the dock panes (right sidebar, bottom panel) at a fixed pixel size
   * while their container changes size.
   *
   * `punica-split` stores pane sizes as percentages, so anything that resizes
   * the container — dragging the primary sidebar, resizing the window — scales
   * every pane with it: widening the sidebar by 100px used to shrink the right
   * sidebar too, and relayout of whatever it holds (a long AI chat) is what
   * made that drag expensive. Only the content pane should absorb the change,
   * which is also what the editors people compare us to do.
   *
   * The user's own gutter drag is untouched: it never changes the container's
   * size, so this never runs during one.
   */
  #pinChromePanes(split: HTMLElement, node: LayoutNode): void {
    if (node.type === 'slot') return;
    if (typeof ResizeObserver === 'undefined') return;

    const pinned = node.children
      .map((child, index) =>
        child.type === 'slot' && PINNED_PANE_ROLES.has(child.role ?? '')
          ? index
          : -1
      )
      .filter((index) => index >= 0);
    if (!pinned.length || pinned.length === node.children.length) return;

    const isRow = node.type === 'row';
    const measure = (): number =>
      isRow ? split.clientWidth : split.clientHeight;

    let lastSize = 0;
    let settle: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver(() => {
      const size = measure();
      if (size <= 0) return;
      const previous = lastSize;
      lastSize = size;
      // First callback only records the starting size — there is nothing to
      // preserve yet.
      if (previous <= 0 || previous === size) return;

      const api = split as unknown as {
        getSizes?: () => number[];
        setSizes?: (sizes: number[]) => void;
      };
      const current = api.getSizes?.();
      if (!current?.length) return;
      const next = sizesPreservingPx(current, previous, size, pinned);
      if (!next) return;

      // Panes animate their flex-basis for 120ms. Correcting the percentages
      // on every frame of a sidebar drag through that animation would make the
      // pinned pane rubber-band behind the pointer, so suppress it while the
      // resizes keep coming and restore it once they stop.
      split.toggleAttribute('no-transition', true);
      api.setSizes?.(next);
      if (settle) clearTimeout(settle);
      settle = setTimeout(() => {
        settle = null;
        split.removeAttribute('no-transition');
      }, PANE_SETTLE_MS);
    });
    observer.observe(split);
    this.#paneObservers.push(observer);
  }

  /** Drop the pane observers of a layout that is being replaced. */
  #disconnectPaneObservers(): void {
    for (const observer of this.#paneObservers) observer.disconnect();
    this.#paneObservers = [];
  }

  /**
   * Apply an ApplicationProfile's shell spec to the layout.
   * Structural slots (activityBar, primarySidebar, contentSidebar,
   * statusbar) are derived from `profile.shell.activeModules` rather
   * than the hardcoded `id === 'welcome'` check in `applyProfile`.
   *
   * Called by ProfileManager during bootstrap and profile-switch.
   * The existing `applyProfile(LayoutProfile)` API is preserved for
   * backward compatibility (tests, hosts that call it directly).
   */
  applyApplicationProfile(appProfile: ApplicationProfile): void {
    const activeModules = new Set(appProfile.shell.activeModules);

    // Derive structural slots from the active module set.
    const hasActivityBar = activeModules.has('shell.activityBar');
    const hasSidebars = activeModules.has('shell.view');
    const hasStatusbar = activeModules.has('shell.statusbar');

    const layout = appProfile.shell.layout;
    const layoutId = layout?.id ?? appProfile.identity.id;
    const layoutRoot = layout?.root ?? DEFAULT_MINIMAL_ROOT;

    this.#activeProfile = {
      id: layoutId,
      title: appProfile.identity.name,
      root: layoutRoot
    };
    this.#slots.clear();
    this.#viewHosts.clear();
    this.#collapsed.clear();
    this.#disconnectPaneObservers();
    this.#layoutElement.innerHTML = '';

    // CSS classes: keep backward-compat names + add profile-specific data attr.
    this.#layoutElement.classList.remove('layout-welcome', 'layout-classic');
    this.#layoutElement.classList.add(
      hasActivityBar ? 'layout-classic' : 'layout-welcome'
    );
    this.#layoutElement.dataset.profileId = appProfile.identity.id;

    if (hasActivityBar) {
      const activityBarHost = document.createElement('div');
      activityBarHost.id = 'activityBar';
      this.#layoutElement.appendChild(activityBarHost);
      this.#slots.set('activityBar', {
        id: 'activityBar',
        role: 'activityBar',
        content: activityBarHost
      });
    }

    const mainContent = document.createElement('div');
    mainContent.id = 'mainContent';
    mainContent.style.height = '100%';
    mainContent.style.width = '100%';
    mainContent.style.display = 'flex';
    this.#layoutElement.appendChild(mainContent);

    if (hasSidebars) {
      const primarySidebarHost = document.createElement('div');
      primarySidebarHost.id = 'primarySidebar';
      this.#layoutElement.appendChild(primarySidebarHost);
      this.#slots.set('primarySidebar', {
        id: 'primarySidebar',
        role: 'primarySidebar',
        content: primarySidebarHost
      });

      const contentSidebarHost = document.createElement('div');
      contentSidebarHost.id = 'contentSidebar';
      this.#layoutElement.appendChild(contentSidebarHost);
      this.#slots.set('contentSidebar', {
        id: 'contentSidebar',
        role: 'overlay',
        content: contentSidebarHost
      });
    }

    if (hasStatusbar) {
      const statusbarHost = document.createElement('div');
      statusbarHost.id = 'statusbar';
      this.#layoutElement.appendChild(statusbarHost);
      this.#slots.set('statusbar', {
        id: 'statusbar',
        role: 'statusbar',
        content: statusbarHost
      });
    }

    this.#buildNode(layoutRoot, mainContent, true);
  }

  render(container: HTMLElement): void {
    // Select profile based on current lifecycle phase. Hosts are responsible
    // for keeping Runtime.runtime.lifecyclePhase up to date.
    //
    // The phase-driven welcome↔classic toggle only governs the two LEGACY
    // layout profiles. When an ApplicationProfile has already been applied
    // (`applyApplicationProfile`) to a non-legacy layout — e.g. a custom or
    // agent-ops profile — render() must NOT clobber it with the phase default.
    // This is the single authority reconciliation: profile wins, phase only
    // toggles between the built-in welcome/classic pair.
    try {
      const anyWindow: any = window as any;
      // Support both legacy `punica.Runtime.runtime` and the current
      // global `punica.runtime` façade exposed by @punica/editor.
      const rt =
        anyWindow.punica?.Runtime?.runtime ?? anyWindow.punica?.runtime;
      const phase: string | undefined = rt?.lifecyclePhase;
      const desired =
        phase === 'noWorkspace' ? welcomeProfile : classicEditorProfile;

      const activeIsLegacy =
        this.#activeProfile.id === classicEditorProfile.id ||
        this.#activeProfile.id === welcomeProfile.id;

      if (!this.#layoutElement.childElementCount) {
        // Nothing built yet — build the phase default.
        this.applyProfile(desired);
      } else if (activeIsLegacy && this.#activeProfile.id !== desired.id) {
        // Only re-toggle between the built-in welcome/classic pair.
        this.applyProfile(desired);
      }
      // Non-legacy active profile with DOM already built → leave it untouched.
    } catch {
      if (!this.#layoutElement.childElementCount) {
        this.applyProfile(this.#activeProfile);
      }
    }

    container.appendChild(this.#layoutElement);
  }
}

type LayoutNamespace = {
  manager: ILayoutManager;
};

const layout: LayoutNamespace = {
  manager: new LayoutManager()
};

export default layout;
